import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  HiCloudArrowUp,
  HiPencil,
  HiArrowDownTray,
  HiTrash,
  HiFolderPlus,
  HiArrowLeft,
  HiArrowUp
} from 'react-icons/hi2';
import { CgSpinner } from 'react-icons/cg';
import { RootState } from '../../redux/reducers';
import PageHeader from '../components/misc/PageHeader';
import PrimaryButton from '../components/buttons/PrimaryButton';
import useSelectedCommunity from '../../hooks/community/useSelectedCommunity';
import {
  setCurrentDirectory,
  setShowCreateFolderModal,
  setShowDeleteConfirmModal,
  clearSelection,
  setLoading,
  setStorageUsage,
  setDirectoryTree,
  setItems,
  setUploading,
  setUploadProgress,
  addItems,
  setStorageLimitError,
  setError,
  setRootBreadcrumbName,
  startRenameItem,
  startRenameDirectory,
  selectItem,
  selectDirectory,
  updateItemData,
  updateDirectoryData,
  moveItemsFromCurrent,
} from '../../redux/slices/storageSlice';
import storageService from '../../services/community/storageService';
import BreadcrumbNav from '../components/files/BreadcrumbNav';
import FileListView from '../components/files/FileListView';
import CreateFolderModal from '../components/files/CreateFolderModal';
import StorageLimitModal from '../components/files/StorageLimitModal';
import DeleteConfirmModal from '../components/files/DeleteConfirmModal';
import { StorageDirectory, StorageItem, StorageLimitError } from '../../types/storage';
import Button from '../components/buttons/Button';
import StorageUsageBar from '../components/files/StorageUsageBar';

export default function FilesPage(): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const community = useSelectedCommunity();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    item: StorageItem | null;
    directory: StorageDirectory | null;
  } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const {
    currentDirectoryId,
    breadcrumbs,
    storageUsage,
    allDirectories,
    items,
    selectedItemIds,
    selectedDirectoryIds,
    sortBy,
    sortOrder,
    searchQuery,
    isLoading,
    isUploading,
    uploadProgress,
    showCreateFolderModal,
    showDeleteConfirmModal,
    showStorageLimitModal,
    storageLimitError,
  } = useSelector((state: RootState) => state.storage);

  const loadData = useCallback(async () => {
    try {
      const usage = await storageService.getStorageUsage();
      dispatch(setStorageUsage(usage));

      dispatch(setLoading(true));
      const tree = await storageService.getDirectoryTree();
      dispatch(setDirectoryTree(tree));

      const currentItems = await storageService.getItems(currentDirectoryId);
      dispatch(setItems(currentItems));
    } catch (error) {
      dispatch(setError((error as Error).message || 'Failed to load data'));
    }
  }, [dispatch, currentDirectoryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (community?.name) dispatch(setRootBreadcrumbName(community.name));
  }, [community?.name, dispatch]);

  // Removed redundant loadItems and its effect. Items are loaded via loadData.
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files || files.length === 0) return;

    try {
      dispatch(setUploading(true));
      const result = await storageService.uploadFiles(
        Array.from(files),
        currentDirectoryId ?? undefined,
        (progressEvent) => {
          dispatch(setUploadProgress(
            progressEvent.total ? Math.round((progressEvent.loaded / progressEvent.total) * 100) : 0
          ));
        }
      );

      if ('usedStorage' in result) {
        dispatch(setStorageLimitError(result as StorageLimitError));
      } else {
        dispatch(addItems(result as StorageItem[]));

        const usage = await storageService.getStorageUsage();
        dispatch(setStorageUsage(usage));
      }
    } catch (error) {
      dispatch(setError((error as Error).message || 'Failed to upload files'));
    } finally {
      dispatch(setUploading(false));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    // Only show overlay for OS file drops, not internal item drags
    if (Array.from(e.dataTransfer.types).includes('Files') &&
        !Array.from(e.dataTransfer.types).includes('application/veraconnect-item')) {
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) setDragOver(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (Array.from(e.dataTransfer.types).includes('Files') &&
        !Array.from(e.dataTransfer.types).includes('application/veraconnect-item')) {
      dragCounterRef.current -= 1;
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setDragOver(false);
      }
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragOver(false);

    // If this is an internal item drag (handled by FileListView), ignore here
    if (e.dataTransfer.types.includes('application/veraconnect-item')) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    try {
      dispatch(setUploading(true));
      const result = await storageService.uploadFiles(
        files,
        currentDirectoryId ?? undefined,
        (progressEvent) => {
          dispatch(setUploadProgress(
            progressEvent.total ? Math.round((progressEvent.loaded / progressEvent.total) * 100) : 0
          ));
        }
      );

      if ('usedStorage' in result) {
        dispatch(setStorageLimitError(result as StorageLimitError));
      } else {
        dispatch(addItems(result as StorageItem[]));
        const usage = await storageService.getStorageUsage();
        dispatch(setStorageUsage(usage));
      }
    } catch (error) {
      dispatch(setError((error as Error).message || 'Failed to upload files'));
    } finally {
      dispatch(setUploading(false));
    }
  };

  const handleContextMenu = (
    e: React.MouseEvent,
    item: StorageItem | null,
    directory: StorageDirectory | null
  ) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item,
      directory,
    });
  };

  const handleDeleteSelected = () => {
    if (selectedItemIds.length > 0 || selectedDirectoryIds.length > 0) {
      dispatch(setShowDeleteConfirmModal(true));
    }
  };

  // compute parent directory id of current folder (null indicates root)
  const parentDirectoryId: number | null =
    currentDirectoryId === null
      ? null
      : allDirectories.find((d) => d.id === currentDirectoryId)?.parentId ?? null;
  const hasParent = currentDirectoryId !== null;

  const handleMoveSelectedToParent = async () => {
    if (!hasParent) return;
    try {
      dispatch(setLoading(true));
      const newDir = parentDirectoryId;
      // move items
      if (selectedItemIds.length > 0) {
        const updates = await Promise.all(
          selectedItemIds.map((id) => storageService.moveItem(id, { newDirectoryId: newDir }))
        );
        updates.forEach((item) => dispatch(updateItemData(item)));
        // remove from current listing if leaving
        if (newDir !== currentDirectoryId) {
          dispatch(
            moveItemsFromCurrent({ itemIds: selectedItemIds, newDirectoryId: newDir })
          );
        }
      }
      if (selectedDirectoryIds.length > 0) {
        const updates = await Promise.all(
          selectedDirectoryIds.map((id) =>
            storageService.moveDirectory(id, { newParentId: newDir })
          )
        );
        updates.forEach((dir) => dispatch(updateDirectoryData(dir)));
      }
      dispatch(clearSelection());
    } catch (err) {
      dispatch(setError((err as Error).message || 'Failed to move items'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleNavigate = (directoryId: number | null) => {
    dispatch(setCurrentDirectory(directoryId));
  };

  const handleGoUp = () => {
    if (currentDirectoryId !== null) {
      const currentDir = allDirectories.find((d) => d.id === currentDirectoryId);
      if (currentDir) {
        dispatch(setCurrentDirectory(currentDir.parentId));
      }
    }
  };

  return (
    <div
      className="h-full flex flex-col"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <PageHeader
        title={community?.name ?? t('page.files.title')}
        subtitle={t('page.files.subtitle')}
        className='pb-1'
        primaryButton={
          <div className='flex gap-2 justify-end'>
            <PrimaryButton
              text={isUploading ? `${t('page.files.uploading')} ${uploadProgress}%` : t('page.files.upload')}
              icon={HiCloudArrowUp}
              onClick={handleUploadClick}
              disabled={isUploading}
            />
            <Button
              variant="secondary"
              onClick={() => dispatch(setShowCreateFolderModal(true))}>
              <HiFolderPlus className="h-4 w-4" />
              {t('page.files.newFolder')}
            </Button>
          </div>
        }
      />

      {/* Toolbar + file list card */}
      <div className="mt-4 flex flex-1 flex-col overflow-hidden rounded-[12px] border border-secondary-200 bg-surface">
        <div className="flex items-center justify-between gap-3 bg-primary-100 px-4 py-3 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="tertiary"
              onClick={handleGoUp}
              disabled={currentDirectoryId === null}
              className="flex items-center gap-2 rounded-[8px] bg-surface"
              title={t('page.files.goUpOneLevel')}
            >
              <HiArrowLeft className="h-4 w-4" />
            </Button>
            <BreadcrumbNav breadcrumbs={breadcrumbs} onNavigate={handleNavigate} />
          </div>

          <div className="flex flex-shrink-0 items-center gap-3">
            <StorageUsageBar usage={storageUsage} />

            {(selectedItemIds.length > 0 || selectedDirectoryIds.length > 0) && (
              <div className="flex items-center gap-2 border-l border-primary-300/40 pl-4">
                <span className="text-sm font-medium text-primary-900">
                  {selectedItemIds.length + selectedDirectoryIds.length} {t('page.files.selected')}
                </span>
                {hasParent && (
                  <Button
                    variant="tertiary"
                    onClick={handleMoveSelectedToParent}
                    className="text-xs"
                  >
                    <HiArrowUp className="w-3 h-3" />
                    {t('page.files.moveToParent')}
                  </Button>
                )}
                <Button
                  variant="danger-v2"
                  onClick={handleDeleteSelected}
                  className="text-xs"
                >
                  {t('page.files.delete')}
                </Button>
                <Button
                  variant="tertiary"
                  onClick={() => dispatch(clearSelection())}
                  className="text-xs"
                >
                  {t('page.files.clear')}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="relative flex-1 overflow-auto p-3 md:p-4">
          {dragOver && (
            <div className="absolute inset-3 z-10 flex items-center justify-center rounded-[12px] border-4 border-dashed border-primary bg-primary-100/30 md:inset-4">
              <div className="text-center">
                <HiCloudArrowUp className="mx-auto mb-4 h-16 w-16 text-primary" />
                <p className="text-lg font-semibold text-primary-900">{t('page.files.dropFilesHere')}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <CgSpinner className="h-12 w-12 animate-spin text-primary" />
            </div>
          ) : (
            <FileListView
              directories={allDirectories}
              items={items}
              selectedItemIds={selectedItemIds}
              selectedDirectoryIds={selectedDirectoryIds}
              sortBy={sortBy}
              sortOrder={sortOrder}
              searchQuery={searchQuery}
              currentDirectoryId={currentDirectoryId}
              onUpload={handleUploadClick}
              onContextMenu={handleContextMenu}
            />
          )}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-secondary-50 rounded-xl shadow-xl border border-secondary-200 py-1 z-50 min-w-[180px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {hasParent && (
            <button
              type="button"
              className="w-full px-4 py-2.5 text-left text-sm text-primary-900 hover:bg-secondary-200 flex items-center gap-2 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                // move single
                const doMove = async () => {
                  try {
                    dispatch(setLoading(true));
                    const parent = parentDirectoryId;
                    if (contextMenu?.item) {
                      const updated = await storageService.moveItem(contextMenu.item.id, { newDirectoryId: parent });
                      dispatch(updateItemData(updated));
                      if (parent !== currentDirectoryId) {
                        dispatch(
                          moveItemsFromCurrent({ itemIds: [contextMenu.item.id], newDirectoryId: parent })
                        );
                      }
                    } else if (contextMenu?.directory) {
                      if (currentDirectoryId !== null && parent !== currentDirectoryId) {
                        const updated = await storageService.moveDirectory(contextMenu.directory.id, { newParentId: parent });
                        dispatch(updateDirectoryData(updated));
                      }
                    }
                  } catch (err) {
                    console.error('Failed to move:', err);
                  } finally {
                    dispatch(setLoading(false));
                    setContextMenu(null);
                  }
                };
                doMove();
              }}
            >
              <HiArrowUp className="w-4 h-4" />
              {t('page.files.moveToParent')}
            </button>
          )}

          <button
            type="button"
            className="w-full px-4 py-2.5 text-left text-sm text-primary-900 hover:bg-secondary-200 flex items-center gap-2 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              if (contextMenu.item) dispatch(startRenameItem(contextMenu.item.id));
              else if (contextMenu.directory) dispatch(startRenameDirectory(contextMenu.directory.id));
              setContextMenu(null);
            }}
          >
            <HiPencil className="w-4 h-4" />
            {t('page.files.rename')}
          </button>
          {contextMenu.item && (
            <button
              type="button"
              className="w-full px-4 py-2.5 text-left text-sm text-primary-900 hover:bg-secondary-200 flex items-center gap-2 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                window.open(contextMenu.item!.fileUrl, '_blank');
                setContextMenu(null);
              }}
            >
              <HiArrowDownTray className="w-4 h-4" />
              {t('page.files.download')}
            </button>
          )}
          <div className="border-t border-secondary-200 my-1" />
          <button
            type="button"
            className="w-full px-4 py-2.5 text-left text-sm text-danger hover:bg-danger/10 flex items-center gap-2 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              dispatch(clearSelection());
              if (contextMenu.item) dispatch(selectItem(contextMenu.item.id));
              else if (contextMenu.directory) dispatch(selectDirectory(contextMenu.directory.id));
              dispatch(setShowDeleteConfirmModal(true));
              setContextMenu(null);
            }}
          >
            <HiTrash className="w-4 h-4" />
            {t('page.files.delete')}
          </button>
        </div>
      )}

      {/* Modals */}
      <CreateFolderModal isOpen={showCreateFolderModal} currentDirectoryId={currentDirectoryId} />
      <DeleteConfirmModal isOpen={showDeleteConfirmModal} />
      <StorageLimitModal isOpen={showStorageLimitModal} error={storageLimitError} />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}
